<script lang="ts">
  import type { Expense } from "../../lib/types";
  import Modal from "../overlay/Modal.svelte";
  import Button from "../input/Button.svelte";
  import { categoryIcons, entryIcons } from "../../lib/icons";
  import { formatCurrencyWithUnit } from "../../lib/utils";

  let {
    show = false,
    expense = null,
    onClose,
  }: {
    show?: boolean;
    expense?: Expense | null;
    onClose?: () => void;
  } = $props();

  const categoryLabels: Record<string, { label: string; icon: string }> = {
    food:          { label: "식비",      icon: categoryIcons.food },
    transport:     { label: "교통",      icon: categoryIcons.transport },
    accommodation: { label: "숙박",      icon: categoryIcons.accommodation },
    shopping:      { label: "쇼핑",      icon: categoryIcons.shopping },
    activity:      { label: "액티비티", icon: categoryIcons.activity },
    other:         { label: "기타",      icon: categoryIcons.other },
  };

  const entryMethodLabels: Record<string, { label: string; icon: string; color: string }> = {
    manual:   { label: "수동 입력",     icon: entryIcons.manual,   color: "bg-gray-100 text-gray-700" },
    ocr:      { label: "영수증 스캔",   icon: entryIcons.scan,     color: "bg-indigo-100 text-indigo-700" },
    imported: { label: "외부 가져오기", icon: entryIcons.imported, color: "bg-green-100 text-green-700" },
  };

  function formatDate(dateStr: string): string {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("ko-KR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }

  function close() {
    onClose?.();
  }
</script>

<Modal
  {show}
  title="지출 상세"
  size="md"
  onClose={close}
>
  {#if expense}
    <div class="space-y-6">
      <!-- 헤더 -->
      <div class="flex items-start justify-between">
        <div class="flex items-center gap-3">
          <div class="w-12 h-12 rounded-full flex items-center justify-center" style="background: rgba(255,255,255,0.08);">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" style="color: var(--accent);">
              {@html categoryLabels[expense.category]?.icon ?? categoryIcons.other}
            </svg>
          </div>
          <div>
            <h3 class="text-lg font-semibold text-gray-900">{expense.merchantName}</h3>
            <p class="text-sm text-gray-500">
              {categoryLabels[expense.category]?.label || "기타"}
            </p>
          </div>
        </div>
        <div class="text-right">
          <p class="text-xl font-bold text-gray-900">
            {formatCurrencyWithUnit(expense.krwAmountCents)}
          </p>
          {#if expense.originalCurrency !== "KRW"}
            <p class="text-sm text-gray-500">
              {formatCurrencyWithUnit(expense.originalAmountCents, expense.originalCurrency)}
            </p>
          {/if}
        </div>
      </div>

      <!-- 입력 방식 표시 -->
      <div class="flex items-center gap-2">
        <span class="px-2 py-1 rounded-full text-xs font-medium {(entryMethodLabels[expense.entryMethod] || entryMethodLabels.manual).color}">
          <svg class="w-3.5 h-3.5 inline-block mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {@html (entryMethodLabels[expense.entryMethod] || entryMethodLabels.manual).icon}
          </svg>{(entryMethodLabels[expense.entryMethod] || entryMethodLabels.manual).label}
        </span>
        {#if expense.hasReceipt}
          <span class="px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1" style="background: rgba(59,130,246,0.15); color: #93c5fd;">
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
            영수증 첨부
          </span>
        {/if}
        {#if expense.modified}
          <span class="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
            ✎ 수정됨
          </span>
        {/if}
      </div>

      <!-- 기본 정보 -->
      <div class="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
        <div>
          <p class="text-xs text-gray-500">결제자</p>
          <p class="font-medium">{expense.payer?.name || "미지정"}</p>
        </div>
        <div>
          <p class="text-xs text-gray-500">거래일</p>
          <p class="font-medium">{formatDate(expense.transactedAt)}</p>
        </div>
        <div>
          <p class="text-xs text-gray-500">정산 상태</p>
          <p class="font-medium">
            {#if expense.settlementStatus === "settled"}
              <span class="text-green-600">✓ 정산 완료</span>
            {:else}
              <span class="text-orange-600">○ 정산 대기</span>
            {/if}
          </p>
        </div>
        {#if expense.exchangeRate && expense.originalCurrency !== "KRW"}
          <div>
            <p class="text-xs text-gray-500">환율</p>
            <p class="font-medium">1 {expense.originalCurrency} = ₩{expense.exchangeRate}</p>
          </div>
        {/if}
      </div>

      <!-- 메모 -->
      {#if expense.description}
        <div class="p-4 bg-gray-50 rounded-lg">
          <p class="text-xs text-gray-500 mb-1">메모</p>
          <p class="text-gray-700">{expense.description}</p>
        </div>
      {/if}

      <!-- OCR 원본 데이터 (영수증 스캔인 경우만) -->
      {#if expense.receipt && expense.entryMethod === "ocr"}
        <div class="border border-indigo-200 rounded-lg overflow-hidden">
          <div class="bg-indigo-50 px-4 py-2 border-b border-indigo-200">
            <h4 class="font-medium text-indigo-800 flex items-center gap-2">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {@html entryIcons.scan}
              </svg> OCR 인식 원본
              {#if expense.receipt.confidenceScore}
                <span class="text-xs text-indigo-600">
                  (정확도: {(expense.receipt.confidenceScore * 100).toFixed(0)}%)
                </span>
              {/if}
            </h4>
          </div>
          <div class="p-4 space-y-3">
            {#if expense.receipt.ocrOriginal}
              <div class="grid grid-cols-2 gap-3 text-sm">
                {#if expense.receipt.ocrOriginal.merchantName}
                  <div>
                    <p class="text-xs text-gray-500">상호명 (원본)</p>
                    <p class="font-medium">{expense.receipt.ocrOriginal.merchantName}</p>
                  </div>
                {/if}
                {#if expense.receipt.ocrOriginal.totalAmount}
                  <div>
                    <p class="text-xs text-gray-500">금액 (원본)</p>
                    <p class="font-medium">
                      {formatCurrencyWithUnit(expense.receipt.ocrOriginal.totalAmount, expense.receipt.ocrOriginal.currency || "KRW")}
                    </p>
                  </div>
                {/if}
                {#if expense.receipt.ocrOriginal.transactionDate}
                  <div>
                    <p class="text-xs text-gray-500">거래일 (원본)</p>
                    <p class="font-medium">{expense.receipt.ocrOriginal.transactionDate}</p>
                  </div>
                {/if}
              </div>

              <!-- 수정된 항목 표시 -->
              {#if expense.receipt.differences && Object.keys(expense.receipt.differences).length > 0}
                <div class="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p class="text-xs font-medium text-amber-800 mb-2 flex items-center gap-1"><svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg> 수정된 항목</p>
                  {#each Object.entries(expense.receipt.differences) as [field, diff]}
                    <div class="text-sm text-amber-700">
                      <span class="font-medium">{field}:</span>
                      <span class="line-through text-gray-400">{diff.ocr}</span>
                      →
                      <span class="font-medium">{diff.current}</span>
                    </div>
                  {/each}
                </div>
              {/if}

              <!-- 품목 내역 -->
              {#if expense.receipt.ocrOriginal.lineItems && expense.receipt.ocrOriginal.lineItems.length > 0}
                <div class="mt-3">
                  <p class="text-xs text-gray-500 mb-2">품목 내역</p>
                  <div class="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100">
                    {#each expense.receipt.ocrOriginal.lineItems as item}
                      <div class="flex justify-between items-center p-2 text-sm">
                        <span>
                          {item.name}
                          {#if item.quantity > 1}
                            <span class="text-gray-400">x{item.quantity}</span>
                          {/if}
                        </span>
                        <span class="font-medium">{formatCurrencyWithUnit(item.price)}</span>
                      </div>
                    {/each}
                  </div>
                </div>
              {/if}
            {/if}

            <!-- 영수증 이미지 -->
            {#if expense.receipt.imageUrl}
              <div class="mt-3">
                <p class="text-xs text-gray-500 mb-2">영수증 이미지</p>
                <img 
                  src={expense.receipt.imageUrl} 
                  alt="영수증" 
                  class="w-full max-h-64 object-contain bg-gray-100 rounded-lg"
                />
              </div>
            {/if}
          </div>
        </div>
      {/if}

      <!-- 참여자 -->
      {#if expense.participants && expense.participants.length > 0}
        <div>
          <p class="text-xs text-gray-500 mb-2">참여자 ({expense.participants.length}명)</p>
          <div class="flex flex-wrap gap-2">
            {#each expense.participants as participant}
              <div class="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-full">
                <span class="text-sm">{participant.name}</span>
                <span class="text-xs text-gray-500">
                  {formatCurrencyWithUnit(participant.shareAmountCents)}
                </span>
              </div>
            {/each}
          </div>
        </div>
      {/if}
    </div>

    <!-- 버튼 -->
    <div class="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
      <Button variant="secondary" onclick={close}>닫기</Button>
    </div>
  {/if}
</Modal>
